import {
    ArrowLeftIcon,
    Cog6ToothIcon,
    PauseIcon,
    PlayIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import * as poseDetection from "@tensorflow-models/pose-detection";
import * as tf from "@tensorflow/tfjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BallTracker } from "../utils/ballSmoother";
import { MotionBallDetector } from "../utils/motionBall";
import { classifyHandedness, nearestPose } from "../utils/poseHandedness";
import { SwingDetector } from "../utils/poseSwing";
import {
    TrackNet,
    loadTrackNetConfig,
    saveTrackNetConfig,
} from "../utils/trackNet";

/**
 * BallTrackerLab — a self-contained MVP for evaluating in-browser computer
 * vision on tennis clips.
 *
 * What it does (steps 1–2 of the CV roadmap, plus a shot-candidate teaser):
 *   1. Frame pipeline: a real <video> element (pixel-accessible) with a
 *      <canvas> overlay drawn on top, synced to playback.
 *   2. Object detection: COCO-SSD runs in the browser and returns `person`
 *      and `sports ball` boxes, drawn live over the video.
 *   3. Bonus: tracks the ball centroid, draws a fading trajectory trail, and
 *      flags a "shot candidate" when the ball's direction changes sharply —
 *      optionally auto-pausing so a human can add detail.
 *
 * IMPORTANT: pixel access requires a same-origin / CORS-enabled video.
 *   - Local files (Choose file) always work.               ✅
 *   - Direct mp4/m3u8 URLs work only with CORS headers.     ⚠️
 *   - YouTube / embedded players cannot be read.            ❌
 */

const BALL_TRAIL_MAX = 40; // points kept for the trajectory trail
const SHOT_ANGLE_THRESH = (110 * Math.PI) / 180; // direction change to flag a hit
const SHOT_MIN_SPEED = 6; // px/frame minimum to reject jitter
const SHOT_DEBOUNCE_S = 0.35; // min seconds between flagged shots
const TN_INTERVAL_MS = 150; // throttle TrackNet inference (heavy per-frame)

function BallTrackerLab() {
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modelRef = useRef(null); // COCO-SSD model
  const trackNetRef = useRef(null); // TrackNet instance
  const poseRef = useRef(null); // MoveNet detector
  const motionRef = useRef(null); // classic motion/blob detector
  const ballTrackerRef = useRef(new BallTracker()); // temporal gate + smoother
  const swingRef = useRef(new SwingDetector()); // pose-based swing detector
  const ballKeysRef = useRef({ prev: null, cur: null }); // interpolation keyframes
  const missCountRef = useRef(0);
  const lastTnRunRef = useRef(0); // last TrackNet inference timestamp
  const lastResultRef = useRef({ persons: [], ball: null }); // reused when throttled
  const rafRef = useRef(null);
  const busyRef = useRef(false);
  const objectUrlRef = useRef(null);

  // Ball tracking state kept in refs (updated every frame, no re-render)
  const trailRef = useRef([]); // [{ x, y, t }]
  const lastShotTimeRef = useRef(-999);

  const [src, setSrc] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [running, setRunning] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [threshold, setThreshold] = useState(0.35);
  const [fps, setFps] = useState(0);
  const [autoPause, setAutoPause] = useState(true);
  const [shots, setShots] = useState([]); // [{ t, x, y }]
  const [counts, setCounts] = useState({ person: 0, ball: 0 });

  // Detector selection & readiness
  const [detector, setDetector] = useState("coco"); // "coco" | "tracknet"
  const [detectPlayers, setDetectPlayers] = useState(true);
  const [cocoReady, setCocoReady] = useState(false);
  const [tnReady, setTnReady] = useState(false);
  const [tnLoading, setTnLoading] = useState(false);
  const [tnStatus, setTnStatus] = useState("");
  const [showTnSettings, setShowTnSettings] = useState(false);
  const [tnConfig, setTnConfig] = useState(() => loadTrackNetConfig());

  // Pose / handedness
  const [poseReady, setPoseReady] = useState(false);
  const [handedness, setHandedness] = useState(true);
  const [dominantHand, setDominantHand] = useState("right");
  const [smoothTrack, setSmoothTrack] = useState(true);
  const [detectError, setDetectError] = useState("");
  const detectErrRef = useRef(false);
  const [tnPeak, setTnPeak] = useState(null);
  const [cocoError, setCocoError] = useState("");

  // Live shots over WebSocket (from scripts/shot_server.py)
  const [wsUrl, setWsUrl] = useState("ws://localhost:8765");
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [liveShots, setLiveShots] = useState([]);
  const [pendingShot, setPendingShot] = useState(null);
  const [procTime, setProcTime] = useState(0);
  const [wsDone, setWsDone] = useState(false);
  const [curTime, setCurTime] = useState(0);
  const wsRef = useRef(null);
  const liveShotsRef = useRef([]);
  const pendingRef = useRef(null);
  const procTimeRef = useRef(0);
  const doneRef = useRef(false);
  const wsMetaRef = useRef(null); // { width, height } coord space of shot x/y from server

  const activeReady =
    detector === "motion"
      ? true
      : detector === "swings"
      ? poseReady
      : detector === "tracknet"
      ? tnReady
      : cocoReady;

  // keep latest tunables available inside the rAF loop without re-binding it
  const optsRef = useRef({
    threshold,
    autoPause,
    detector,
    detectPlayers,
    handedness,
    dominantHand,
    smoothTrack,
  });
  useEffect(() => {
    optsRef.current = {
      threshold,
      autoPause,
      detector,
      detectPlayers,
      handedness,
      dominantHand,
      smoothTrack,
    };
  }, [
    threshold,
    autoPause,
    detector,
    detectPlayers,
    handedness,
    dominantHand,
    smoothTrack,
  ]);

  // ── Load MoveNet (multi-pose) once, for forehand/backhand at shot contact ────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Ensure a usable TF backend before loading the model (webgpu is the
        // default priority but isn't initialized until tf.ready()).
        try {
          await tf.setBackend("webgl");
        } catch {
          /* fall back to default */
        }
        await tf.ready();
        const detectorInstance = await poseDetection.createDetector(
          poseDetection.SupportedModels.MoveNet,
          {
            modelType:
              poseDetection.movenet.modelType.MULTIPOSE_LIGHTNING,
          }
        );
        if (cancelled) return;
        poseRef.current = detectorInstance;
        setPoseReady(true);
      } catch (err) {
        console.error("Failed to load MoveNet", err);
      }
    })();
    return () => {
      cancelled = true;
      poseRef.current?.dispose?.();
    };
  }, []);

  // ── Load COCO-SSD once (players, and ball in "coco" mode) ─────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        try {
          await tf.setBackend("webgl");
        } catch {
          /* fall back to default */
        }
        await tf.ready();
        const model = await cocoSsd.load({ base: "lite_mobilenet_v2" });
        if (cancelled) return;
        modelRef.current = model;
        setCocoReady(true);
      } catch (err) {
        console.error("Failed to load COCO-SSD", err);
        if (!cancelled)
          setCocoError(
            "COCO-SSD failed to load: " +
              (err?.message || err) +
              " — the model downloads from storage.googleapis.com; check your network/ad-blocker/offline state."
          );
      }
    })();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      trackNetRef.current?.dispose?.();
    };
  }, []);

  // ── Load / reload the TrackNet ball model on demand ──────────────────────────
  const loadTrackNet = async (cfg) => {
    const config = cfg ?? tnConfig;
    const url = config.modelUrl?.trim();
    if (!url) {
      setTnStatus("Enter a TrackNet model URL first.");
      setTnReady(false);
      return;
    }
    // Guard against the common format/URL mismatch.
    if (config.backend === "onnx" && /\.json(\?|#|$)/i.test(url)) {
      setTnStatus(
        'ONNX format expects a single .onnx file. For a model.json, switch Model format to "TensorFlow.js".'
      );
      setTnReady(false);
      return;
    }
    if (config.backend === "tfjs" && /\.onnx(\?|#|$)/i.test(url)) {
      setTnStatus(
        'TensorFlow.js format expects a model.json. For a .onnx file, switch Model format to "ONNX".'
      );
      setTnReady(false);
      return;
    }
    setTnLoading(true);
    setTnReady(false);
    setTnStatus("Loading model…");
    try {
      trackNetRef.current?.dispose?.();
      const tn = new TrackNet(config);
      await tn.load();
      trackNetRef.current = tn;
      setTnReady(true);
      setTnStatus("TrackNet model ready.");
      detectErrRef.current = false;
      setDetectError("");
    } catch (err) {
      console.error("TrackNet load failed", err);
      trackNetRef.current = null;
      setTnStatus(`Failed to load model: ${err.message}`);
    } finally {
      setTnLoading(false);
    }
  };

  // ── Source handling ──────────────────────────────────────────────────────────
  const applyUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    resetTracking();
    setSrc(trimmed);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    resetTracking();
    setSrc(url);
    e.target.value = "";
  };

  // Assign crossOrigin BEFORE src so CORS-enabled videos aren't canvas-tainted.
  // (Setting them as JSX props doesn't guarantee this order.)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (src) {
      v.crossOrigin = "anonymous";
      v.src = src;
      v.load();
    } else {
      v.removeAttribute("src");
      v.load();
    }
  }, [src]);

  const resetTracking = () => {
    trailRef.current = [];
    lastShotTimeRef.current = -999;
    trackNetRef.current?.reset?.();
    motionRef.current?.reset?.();
    ballTrackerRef.current?.reset?.();
    swingRef.current?.reset?.();
    ballKeysRef.current = { prev: null, cur: null };
    missCountRef.current = 0;
    setShots([]);
    setCounts({ person: 0, ball: 0 });
  };

  // Interpolate/extrapolate the ball marker from the last two samples so it
  // glides smoothly between throttled TrackNet inferences.
  const interpBall = (tNow) => {
    const { prev, cur } = ballKeysRef.current;
    if (!cur) return null;
    if (prev && cur.t > prev.t) {
      const span = cur.t - prev.t;
      const vx = (cur.x - prev.x) / span;
      const vy = (cur.y - prev.y) / span;
      const maxDt = (TN_INTERVAL_MS / 1000) * 1.5;
      const dt = Math.min(Math.max(tNow - cur.t, 0), maxDt);
      return { x: cur.x + vx * dt, y: cur.y + vy * dt, score: cur.score };
    }
    return { x: cur.x, y: cur.y, score: cur.score };
  };

  // ── Detection loop ───────────────────────────────────────────────────────────
  const startLoop = () => {
    if (rafRef.current) return;
    let frames = 0;
    let fpsClock = performance.now();

    const tick = async () => {
      const video = videoRef.current;
      if (video && !video.paused && !video.ended && !busyRef.current) {
        busyRef.current = true;
        try {
          const {
            threshold: thr,
            detector: mode,
            detectPlayers: wantPlayers,
          } = optsRef.current;
          let result = { persons: [], ball: null };
          let ranDetection = true;

          if (mode === "tracknet") {
            const tn = trackNetRef.current;
            // capture a frame EVERY tick so the 3 stacked frames stay
            // temporally consecutive (TrackNet relies on inter-frame motion)
            if (tn) tn.pushFrame(video);
            const now0 = performance.now();
            if (now0 - lastTnRunRef.current < TN_INTERVAL_MS) {
              // throttled — interpolate the ball forward for a smooth marker
              result = {
                persons: lastResultRef.current.persons,
                ball: interpBall(video.currentTime),
              };
              ranDetection = false;
            } else {
              // Ball from TrackNet (isolated so a failure still lets players draw)
              if (tn) {
                try {
                  const b = await tn.detect(); // inference on the buffered frames
                  if (b) result.ball = b; // { x, y, score }
                  if (detectErrRef.current) {
                    detectErrRef.current = false;
                    setDetectError("");
                  }
                } catch (e) {
                  if (!detectErrRef.current) {
                    detectErrRef.current = true;
                    setDetectError(
                      "TrackNet inference failed: " +
                        (e?.message || e) +
                        " — check TrackNet setup (this model needs Input 640×360, Frames 3, Channels-first ON, Output mode Argmax)."
                    );
                    console.error(e);
                  }
                }
              }
              // Players still from COCO-SSD (optional; useful for handedness later)
              if (wantPlayers && modelRef.current) {
                const preds = await modelRef.current.detect(video, 20, thr);
                result.persons = preds
                  .filter((p) => p.class === "person")
                  .map((p) => ({ bbox: p.bbox, score: p.score }));
              }
              // reset throttle AFTER inference so the next gap is used to
              // capture fresh consecutive frames
              lastTnRunRef.current = performance.now();
              lastResultRef.current = result;
            }
          } else if (mode === "swings") {
            // Pose-based swing detection (no ball needed)
            const now0 = performance.now();
            if (now0 - lastTnRunRef.current < TN_INTERVAL_MS) {
              result = lastResultRef.current;
              ranDetection = false;
            } else {
              if (poseRef.current) {
                try {
                  const poses = await poseRef.current.estimatePoses(video, {
                    maxPoses: 4,
                    flipHorizontal: false,
                  });
                  result.poses = poses;
                  const vw = video.videoWidth || 1280;
                  const vh = video.videoHeight || 720;
                  const events = swingRef.current.update(
                    poses,
                    video.currentTime,
                    vw,
                    vh,
                    optsRef.current.dominantHand
                  );
                  if (events.length) {
                    const last = events[events.length - 1];
                    result.swing = last;
                    setShots((prev) => [
                      ...prev,
                      ...events.map((e) => ({
                        t: e.t,
                        x: e.x,
                        y: e.y,
                        handed: e.handed,
                      })),
                    ]);
                    if (optsRef.current.autoPause) videoRef.current?.pause();
                  }
                } catch (e) {
                  console.error("pose swing error", e);
                }
              }
              lastTnRunRef.current = performance.now();
              lastResultRef.current = result;
            }
          } else if (mode === "motion") {
            // Ball from classic motion/blob detection (no model needed)
            if (!motionRef.current) motionRef.current = new MotionBallDetector();
            const prev =
              trailRef.current[trailRef.current.length - 1] || null;
            const b = motionRef.current.detect(video, prev);
            if (b) result.ball = b;
            if (wantPlayers && modelRef.current) {
              const preds = await modelRef.current.detect(video, 20, thr);
              result.persons = preds
                .filter((p) => p.class === "person")
                .map((p) => ({ bbox: p.bbox, score: p.score }));
            }
          } else {
            // COCO-SSD for both players and the (generic) ball
            const model = modelRef.current;
            if (model) {
              const preds = await model.detect(video, 20, thr);
              for (const p of preds) {
                if (p.class === "person") {
                  result.persons.push({ bbox: p.bbox, score: p.score });
                } else if (p.class === "sports ball") {
                  const [x, y, w, h] = p.bbox;
                  const cand = {
                    x: x + w / 2,
                    y: y + h / 2,
                    score: p.score,
                    bbox: p.bbox,
                  };
                  if (!result.ball || cand.score > result.ball.score)
                    result.ball = cand;
                }
              }
            }
          }

          drawDetections(result);
          if (ranDetection) {
            // temporal gate + smooth on freshly detected balls
            if (optsRef.current.smoothTrack) {
              const vw = video.videoWidth || 1280;
              const vh = video.videoHeight || 720;
              result.ball = ballTrackerRef.current.update(
                result.ball,
                vw,
                vh,
                video.currentTime
              );
              if (mode === "tracknet") lastResultRef.current = result;
              drawDetections(result);
            }
            // update interpolation keyframes from the finalized detection
            const b = result.ball;
            const keys = ballKeysRef.current;
            if (b) {
              keys.prev = keys.cur;
              keys.cur = {
                x: b.x,
                y: b.y,
                t: video.currentTime,
                score: b.score,
              };
              missCountRef.current = 0;
            } else {
              missCountRef.current++;
              if (missCountRef.current >= 2) {
                keys.prev = null;
                keys.cur = null;
              }
            }
            const cand = trackBall(result.ball, video.currentTime);
            if (cand) {
              let handed = null;
              const { handedness: wantHanded, dominantHand: dom } =
                optsRef.current;
              if (wantHanded && poseRef.current) {
                try {
                  const poses = await poseRef.current.estimatePoses(video, {
                    maxPoses: 4,
                    flipHorizontal: false,
                  });
                  const hitPose = nearestPose(poses, cand);
                  if (hitPose) {
                    handed = classifyHandedness(hitPose, cand, dom);
                    drawPose(hitPose, cand);
                  }
                } catch (e) {
                  console.error("pose error", e);
                }
              }
              setShots((prev) => [...prev, { ...cand, handed }]);
              if (optsRef.current.autoPause) videoRef.current?.pause();
            }
          }

          // fps meter
          frames++;
          const now = performance.now();
          if (now - fpsClock >= 500) {
            setFps(Math.round((frames * 1000) / (now - fpsClock)));
            if (mode === "tracknet") {
              setTnPeak(trackNetRef.current?.debugScore ?? null);
            }
            frames = 0;
            fpsClock = now;
          }
        } catch (err) {
          console.error("detect error", err);
        } finally {
          busyRef.current = false;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  useEffect(() => {
    if (running && activeReady) startLoop();
    else stopLoop();
    return stopLoop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, activeReady]);

  // ── Canvas sizing to match the video's intrinsic resolution ──────────────────
  const syncCanvasSize = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
    }
  };

  // ── Drawing ──────────────────────────────────────────────────────────────────
  const drawDetections = (result) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    syncCanvasSize();
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const persons = result?.persons ?? [];
    const ball = result?.ball ?? null;
    const poses = result?.poses ?? [];
    const swing = result?.swing ?? null;

    ctx.lineWidth = Math.max(2, canvas.width / 480);
    ctx.font = `${Math.max(12, canvas.width / 60)}px sans-serif`;

    // pose skeletons (swing mode)
    if (poses.length) {
      const S = 0.3;
      const EDGES = [
        [5, 7], [7, 9], [6, 8], [8, 10], [5, 6], [5, 11],
        [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
      ];
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = Math.max(2, canvas.width / 500);
      for (const pose of poses) {
        const kp = pose.keypoints;
        for (const [a, b] of EDGES) {
          const ka = kp[a];
          const kb = kp[b];
          if ((ka?.score ?? 0) >= S && (kb?.score ?? 0) >= S) {
            ctx.beginPath();
            ctx.moveTo(ka.x, ka.y);
            ctx.lineTo(kb.x, kb.y);
            ctx.stroke();
          }
        }
      }
    }
    // swing contact highlight
    if (swing) {
      ctx.strokeStyle = "#f472b6";
      ctx.lineWidth = Math.max(2, canvas.width / 260);
      ctx.beginPath();
      ctx.arc(swing.x, swing.y, Math.max(10, canvas.width / 70), 0, Math.PI * 2);
      ctx.stroke();
      const label = swing.handed ? swing.handed.type : "swing";
      ctx.fillStyle = "#f472b6";
      const tw = ctx.measureText(label).width;
      ctx.fillRect(swing.x - tw / 2 - 4, Math.max(0, swing.y - 34), tw + 8, 18);
      ctx.fillStyle = "#000";
      ctx.fillText(label, swing.x - tw / 2, Math.max(12, swing.y - 20));
    }

    // player boxes
    for (const p of persons) {
      const [x, y, w, h] = p.bbox;
      ctx.strokeStyle = "#22c55e";
      ctx.strokeRect(x, y, w, h);
      const label = `person ${(p.score * 100) | 0}%`;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "#22c55e";
      ctx.fillRect(x, Math.max(0, y - 18), tw + 8, 18);
      ctx.fillStyle = "#000";
      ctx.fillText(label, x + 4, Math.max(12, y - 4));
    }

    // ball marker — box when available (COCO), else a ring (TrackNet point)
    if (ball) {
      ctx.strokeStyle = "#facc15";
      if (ball.bbox) {
        const [x, y, w, h] = ball.bbox;
        ctx.strokeRect(x, y, w, h);
      } else {
        const r = Math.max(6, canvas.width / 120);
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      const label = `ball ${(ball.score * 100) | 0}%`;
      const tw = ctx.measureText(label).width;
      const lx = ball.bbox ? ball.bbox[0] : ball.x - tw / 2;
      const ly = ball.bbox ? ball.bbox[1] : ball.y - 12;
      ctx.fillStyle = "#facc15";
      ctx.fillRect(lx, Math.max(0, ly - 18), tw + 8, 18);
      ctx.fillStyle = "#000";
      ctx.fillText(label, lx + 4, Math.max(12, ly - 4));
    }

    // fading ball trajectory trail
    const trail = trailRef.current;
    if (trail.length > 1) {
      for (let i = 1; i < trail.length; i++) {
        const a = trail[i - 1];
        const b = trail[i];
        const alpha = i / trail.length;
        ctx.strokeStyle = `rgba(250, 204, 21, ${alpha.toFixed(2)})`;
        ctx.lineWidth = Math.max(2, canvas.width / 400);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }

    const ballCount = ball ? 1 : 0;
    setCounts((prev) =>
      prev.person === persons.length && prev.ball === ballCount
        ? prev
        : { person: persons.length, ball: ballCount }
    );
  };

  // ── Ball tracking + shot-candidate heuristic ─────────────────────────────────
  const trackBall = (ball, t) => {
    if (!ball) return;
    const cx = ball.x;
    const cy = ball.y;

    const trail = trailRef.current;
    trail.push({ x: cx, y: cy, t });
    if (trail.length > BALL_TRAIL_MAX) trail.shift();

    // need three points to measure a direction change
    if (trail.length >= 3) {
      const p0 = trail[trail.length - 3];
      const p1 = trail[trail.length - 2];
      const p2 = trail[trail.length - 1];
      const v1 = { x: p1.x - p0.x, y: p1.y - p0.y };
      const v2 = { x: p2.x - p1.x, y: p2.y - p1.y };
      const m1 = Math.hypot(v1.x, v1.y);
      const m2 = Math.hypot(v2.x, v2.y);
      if (m1 > SHOT_MIN_SPEED && m2 > SHOT_MIN_SPEED) {
        const dot = v1.x * v2.x + v1.y * v2.y;
        const angle = Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2))));
        const debounced = t - lastShotTimeRef.current > SHOT_DEBOUNCE_S;
        if (angle > SHOT_ANGLE_THRESH && debounced) {
          lastShotTimeRef.current = t;
          return { t, x: cx, y: cy };
        }
      }
    }
    return null;
  };

  // ── Draw a pose skeleton + contact marker (shown on the paused shot frame) ──
  const drawPose = (pose, contact) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const kp = pose.keypoints;
    const S = 0.3;
    const EDGES = [
      [5, 7], [7, 9], [6, 8], [8, 10], [5, 6], [5, 11],
      [6, 12], [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
    ];
    ctx.strokeStyle = "#38bdf8";
    ctx.lineWidth = Math.max(2, canvas.width / 400);
    for (const [a, b] of EDGES) {
      const ka = kp[a];
      const kb = kp[b];
      if ((ka?.score ?? 0) >= S && (kb?.score ?? 0) >= S) {
        ctx.beginPath();
        ctx.moveTo(ka.x, ka.y);
        ctx.lineTo(kb.x, kb.y);
        ctx.stroke();
      }
    }
    ctx.fillStyle = "#38bdf8";
    for (const k of kp) {
      if ((k.score ?? 0) >= S) {
        ctx.beginPath();
        ctx.arc(k.x, k.y, Math.max(2, canvas.width / 300), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // contact marker
    ctx.strokeStyle = "#f472b6";
    ctx.lineWidth = Math.max(2, canvas.width / 300);
    ctx.beginPath();
    ctx.arc(contact.x, contact.y, Math.max(8, canvas.width / 90), 0, Math.PI * 2);
    ctx.stroke();
  };

  // ── Playback controls ────────────────────────────────────────────────────────
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const seekTo = (t) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = t;
      drawDetections({ persons: [], ball: null }); // clear stale boxes while paused
    }
  };

  const fmt = (s) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}.${String(
      Math.floor((s % 1) * 10)
    )}`;

  // ── Live shots (WebSocket) ────────────────────────────────────────────────────
  // Review mode: seek to the earliest un-handled shot and pause for confirmation.
  // (CPU detection is slower than realtime, so we step through shots as they
  // arrive rather than trying to keep realtime playback in sync.)
  const maybeReviewNext = () => {
    if (pendingRef.current) return;
    const v = videoRef.current;
    if (!v) return;
    for (const s of liveShotsRef.current) {
      if (s.confirmed || s.skipped) continue;
      pendingRef.current = s;
      setPendingShot(s);
      v.pause();
      v.currentTime = s.time;
      return;
    }
  };

  const wsConnect = () => {
    try {
      wsRef.current?.close();
    } catch {
      /* noop */
    }
    liveShotsRef.current = [];
    pendingRef.current = null;
    procTimeRef.current = 0;
    doneRef.current = false;
    setLiveShots([]);
    setPendingShot(null);
    setProcTime(0);
    setWsDone(false);
    setWsStatus("connecting");
    try {
      const ws = new WebSocket(wsUrl.trim());
      wsRef.current = ws;
      ws.onopen = () => setWsStatus("connected");
      ws.onclose = () => setWsStatus("disconnected");
      ws.onerror = () => setWsStatus("error");
      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.type === "meta") {
          if (msg.width && msg.height) {
            wsMetaRef.current = { width: msg.width, height: msg.height };
          }
        } else if (msg.type === "shot") {
          const arr = liveShotsRef.current;
          arr.push({ time: msg.time, x: msg.x, y: msg.y, confirmed: false, skipped: false });
          arr.sort((a, b) => a.time - b.time);
          setLiveShots([...arr]);
          maybeReviewNext();
        } else if (msg.type === "progress") {
          procTimeRef.current = msg.time;
          setProcTime(msg.time);
        } else if (msg.type === "done") {
          doneRef.current = true;
          setWsDone(true);
        }
      };
    } catch {
      setWsStatus("error");
    }
  };

  const wsDisconnect = () => {
    try {
      wsRef.current?.close();
    } catch {
      /* noop */
    }
    wsRef.current = null;
    setWsStatus("disconnected");
  };

  useEffect(
    () => () => {
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    },
    []
  );

  // Keep the on-screen video time updated (display only).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setCurTime(video.currentTime);
    video.addEventListener("timeupdate", onTime);
    return () => video.removeEventListener("timeupdate", onTime);
  }, [src]);

  const confirmShot = () => {
    const s = pendingRef.current;
    if (s) {
      s.confirmed = true;
      setShots((prev) => [...prev, { t: s.time, x: s.x, y: s.y, handed: null }]);
      setLiveShots([...liveShotsRef.current]);
    }
    pendingRef.current = null;
    setPendingShot(null);
    maybeReviewNext();
  };

  const skipShot = () => {
    const s = pendingRef.current;
    if (s) {
      s.skipped = true;
      setLiveShots([...liveShotsRef.current]);
    }
    pendingRef.current = null;
    setPendingShot(null);
    maybeReviewNext();
  };

  // Reset both sides to the start.
  const wsReset = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
    liveShotsRef.current = [];
    setLiveShots([]);
    pendingRef.current = null;
    setPendingShot(null);
    procTimeRef.current = 0;
    setProcTime(0);
    doneRef.current = false;
    setWsDone(false);
    try {
      wsRef.current?.send(JSON.stringify({ type: "reset" }));
    } catch {
      /* noop */
    }
  };

  // Draw the Python-detected ball on the paused frame so the user can verify.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!pendingShot) {
      if (!running) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    syncCanvasSize();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // The server sends x/y in its own frame resolution (OpenCV's reported
    // width/height), which may differ from the browser's decoded videoWidth/
    // videoHeight. Scale into the canvas coordinate space so the marker lands
    // where the ball actually is on the displayed (CSS-scaled) frame.
    const meta = wsMetaRef.current;
    const sx = meta && meta.width ? canvas.width / meta.width : 1;
    const sy = meta && meta.height ? canvas.height / meta.height : 1;
    const x = pendingShot.x * sx;
    const y = pendingShot.y * sy;
    // eslint-disable-next-line no-console
    console.log(
      "[marker] raw=(%s,%s) meta=%o canvas=%dx%d video=%dx%d css=%dx%d -> draw=(%d,%d)",
      pendingShot.x,
      pendingShot.y,
      meta,
      canvas.width,
      canvas.height,
      videoRef.current?.videoWidth,
      videoRef.current?.videoHeight,
      Math.round(canvas.getBoundingClientRect().width),
      Math.round(canvas.getBoundingClientRect().height),
      x | 0,
      y | 0
    );
    const r = Math.max(12, canvas.width / 60);
    ctx.strokeStyle = "#facc15";
    ctx.lineWidth = Math.max(3, canvas.width / 220);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    // crosshair
    ctx.beginPath();
    ctx.moveTo(x - r * 1.8, y);
    ctx.lineTo(x + r * 1.8, y);
    ctx.moveTo(x, y - r * 1.8);
    ctx.lineTo(x, y + r * 1.8);
    ctx.stroke();
    // label
    const label = "detected ball";
    ctx.font = `${Math.max(12, canvas.width / 55)}px sans-serif`;
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "#facc15";
    ctx.fillRect(x - tw / 2 - 4, Math.max(0, y - r - 22), tw + 8, 18);
    ctx.fillStyle = "#000";
    ctx.fillText(label, x - tw / 2, Math.max(12, y - r - 8));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingShot, running]);

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-base-300 bg-base-100">
        <button className="btn btn-ghost btn-sm btn-square" onClick={() => navigate(-1)} title="Back">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Ball Tracker Lab</p>
          <p className="text-xs text-base-content/50">
            In-browser CV demo — person + ball detection, trajectory & shot candidates
          </p>
        </div>
        <span
          className={`badge badge-sm ${
            activeReady ? "badge-success" : "badge-ghost"
          }`}
        >
          {detector === "motion"
            ? "Motion (no model)"
            : detector === "tracknet"
            ? tnReady
              ? "TrackNet ready"
              : tnLoading
              ? "loading TrackNet…"
              : "TrackNet not loaded"
            : cocoReady
            ? "COCO-SSD ready"
            : "loading COCO-SSD…"}
        </span>
      </div>

      {/* Source controls */}
      <div className="shrink-0 border-b border-base-300 bg-base-100 px-4 py-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          className="input input-bordered input-sm flex-1 min-w-[200px] text-xs"
          placeholder="Paste a CORS-enabled mp4/m3u8 URL…"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyUrl()}
        />
        <button className="btn btn-primary btn-sm" disabled={!urlInput.trim()} onClick={applyUrl}>
          Load URL
        </button>
        <span className="text-xs text-base-content/40">or</span>
        <label className="btn btn-outline btn-sm">
          Choose file…
          <input type="file" accept="video/*" className="hidden" onChange={handleFile} />
        </label>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto w-full px-4 py-4 flex flex-col gap-4">
          {detectError && (
            <div className="alert alert-error text-sm">
              <span>{detectError}</span>
            </div>
          )}
          {cocoError && (
            <div className="alert alert-warning text-sm">
              <span>{cocoError}</span>
            </div>
          )}
          {/* Video + overlay */}
          <div className="relative w-full bg-black rounded-box overflow-hidden">
            {src ? (
              <video
                ref={videoRef}
                playsInline
                className="block w-full h-auto"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onLoadedMetadata={syncCanvasSize}
              />
            ) : (
              <div className="flex h-64 items-center justify-center text-base-content/30 text-sm">
                Load a local file or CORS-enabled video URL to begin
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="pointer-events-none absolute inset-0 w-full h-full"
            />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <button className="btn btn-sm btn-primary gap-1" onClick={togglePlay} disabled={!src}>
              {playing ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
              {playing ? "Pause" : "Play"}
            </button>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="toggle toggle-sm toggle-primary"
                checked={running}
                onChange={(e) => setRunning(e.target.checked)}
                disabled={!activeReady}
              />
              Detection {running ? "on" : "off"}
            </label>

            {/* Ball detector selector */}
            <label className="flex items-center gap-2 text-sm">
              Ball detector
              <select
                className="select select-bordered select-xs"
                value={detector}
                onChange={(e) => setDetector(e.target.value)}
              >
                <option value="coco">COCO-SSD (generic)</option>
                <option value="motion">Motion (no model)</option>
                <option value="tracknet">TrackNet (tennis)</option>
                <option value="swings">Pose swings (no ball)</option>
              </select>
            </label>

            {detector === "tracknet" && (
              <button
                className="btn btn-ghost btn-xs gap-1"
                onClick={() => setShowTnSettings(true)}
                title="TrackNet model settings"
              >
                <Cog6ToothIcon className="w-4 h-4" /> TrackNet setup
              </button>
            )}
            {detector !== "coco" && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="toggle toggle-xs"
                  checked={detectPlayers}
                  onChange={(e) => setDetectPlayers(e.target.checked)}
                />
                Players
              </label>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={autoPause}
                onChange={(e) => setAutoPause(e.target.checked)}
              />
              Auto-pause on shot
            </label>

            {/* Forehand/backhand via pose */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={smoothTrack}
                onChange={(e) => setSmoothTrack(e.target.checked)}
              />
              Smooth/gate
            </label>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={handedness}
                onChange={(e) => setHandedness(e.target.checked)}
                disabled={!poseReady}
                title={poseReady ? "" : "MoveNet still loading…"}
              />
              Handedness
            </label>
            <label className="flex items-center gap-2 text-sm">
              Dominant
              <select
                className="select select-bordered select-xs"
                value={dominantHand}
                onChange={(e) => setDominantHand(e.target.value)}
              >
                <option value="right">Right</option>
                <option value="left">Left</option>
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm">
              Confidence
              <input
                type="range"
                min="0.1"
                max="0.8"
                step="0.05"
                value={threshold}
                onChange={(e) => setThreshold(parseFloat(e.target.value))}
                className="range range-xs w-32"
              />
              <span className="tabular-nums w-8">{threshold.toFixed(2)}</span>
            </label>

            <div className="ml-auto flex items-center gap-3 text-xs text-base-content/60 tabular-nums">
              <span>{fps} fps</span>
              {detector === "tracknet" && (
                <span title="TrackNet peak confidence (before threshold)">
                  peak {tnPeak == null ? "–" : tnPeak.toFixed(2)}
                </span>
              )}
              <span className="text-success">● {counts.person} player(s)</span>
              <span className="text-warning">● {counts.ball} ball</span>
            </div>
          </div>

          {/* Live shots (WebSocket) */}
          <div className="border border-base-300 rounded-box overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-base-200 text-sm font-semibold">
              <span>Live shots (WebSocket)</span>
              <span
                className={`badge badge-sm ${
                  wsStatus === "connected"
                    ? "badge-success"
                    : wsStatus === "connecting"
                    ? "badge-warning"
                    : wsStatus === "error"
                    ? "badge-error"
                    : "badge-ghost"
                }`}
              >
                {wsStatus}
              </span>
              <input
                type="text"
                className="input input-bordered input-xs w-52 font-mono"
                value={wsUrl}
                onChange={(e) => setWsUrl(e.target.value)}
                disabled={wsStatus === "connected" || wsStatus === "connecting"}
              />
              {wsStatus === "connected" || wsStatus === "connecting" ? (
                <button className="btn btn-xs" onClick={wsDisconnect}>
                  Disconnect
                </button>
              ) : (
                <button className="btn btn-xs btn-primary" onClick={wsConnect}>
                  Connect
                </button>
              )}
              <button className="btn btn-xs" onClick={wsReset} title="Restart video + server from the beginning">
                Reset
              </button>
              <span className="ml-auto text-xs font-normal text-base-content/60 tabular-nums">
                video {fmt(curTime)} · {liveShots.length} shots · processed {fmt(procTime)}
                {wsDone ? " · done" : ""}
              </span>
            </div>
            <p className="px-4 py-2 text-xs text-base-content/50">
              Run <code>python3 scripts/shot_server.py public/models/tracknet.onnx
              /path/clip.mp4</code>, load the same clip here, and Connect. You don’t
              need to press play — as each shot is detected the video jumps to it and
              pauses for you to Confirm / Skip, then advances to the next.
            </p>
          </div>

          {/* Shot candidates */}
          <div className="border border-base-300 rounded-box overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-base-200 text-sm font-semibold">
              <span>
                Shot candidates{" "}
                <span className="font-normal text-base-content/50">({shots.length})</span>
              </span>
              {shots.length > 0 && (
                <button className="btn btn-ghost btn-xs gap-1" onClick={() => setShots([])}>
                  <TrashIcon className="w-3.5 h-3.5" /> Clear
                </button>
              )}
            </div>
            {shots.length === 0 ? (
              <p className="px-4 py-3 text-xs text-base-content/50">
                None yet. Turn Detection on and play — a candidate is flagged when the tracked
                ball changes direction sharply. Click a timestamp to jump back and review.
              </p>
            ) : (
              <ul className="divide-y divide-base-200 max-h-56 overflow-y-auto">
                {shots.map((s, i) => (
                  <li key={i} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className="badge badge-sm badge-warning">{i + 1}</span>
                    <button className="link tabular-nums" onClick={() => seekTo(Math.max(0, s.t - 0.3))}>
                      {fmt(s.t)}
                    </button>
                    {s.handed ? (
                      <span
                        className={`badge badge-sm ${
                          s.handed.type === "forehand"
                            ? "badge-primary"
                            : "badge-secondary"
                        }`}
                        title={`contact on player's ${s.handed.side} side`}
                      >
                        {s.handed.type}
                      </span>
                    ) : (
                      <span className="badge badge-sm badge-ghost">—</span>
                    )}
                    <span className="text-xs text-base-content/40">
                      ball at ({s.x | 0}, {s.y | 0})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-base-content/40 leading-relaxed">
            Notes: <strong>Motion (no model)</strong> diffs consecutive frames to find a small
            moving blob — zero setup, but noisy with camera motion/busy backgrounds.
            <strong> COCO-SSD</strong> is a general detector (person + generic “sports ball”).
            <strong> TrackNet</strong> (load a TF.js/ONNX model via <em>TrackNet setup</em>)
            tracks the fast, blurred tennis ball far more reliably. When <strong>Handedness</strong>
            is on, MoveNet estimates the hitting player’s pose at each shot and guesses
            forehand/backhand from which side of the body the contact is on (relative to the
            chosen dominant hand) — treat it as a suggestion to confirm, not ground truth.
            Pixel access needs a local file or CORS-enabled URL; YouTube/embeds can’t be read.
          </p>
        </div>
      </div>

      {/* TrackNet settings modal */}
      {showTnSettings && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-md flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" /> TrackNet Model
              </h3>
              <button
                className="btn btn-ghost btn-sm btn-square"
                onClick={() => setShowTnSettings(false)}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-base-content/60 leading-relaxed">
              Load a TrackNet model in one of two formats, served over HTTP/CORS:{" "}
              <strong>TF.js</strong> (a <code>model.json</code> + weight shards) or{" "}
              <strong>ONNX</strong> (a single <code>.onnx</code> file, run via
              onnxruntime-web — no Python/conversion needed). The input/layout fields
              must match how your model was exported.
            </p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">Model format</label>
              <select
                className="select select-bordered select-sm w-full"
                value={tnConfig.backend}
                onChange={(e) =>
                  setTnConfig({ ...tnConfig, backend: e.target.value })
                }
              >
                <option value="tfjs">TensorFlow.js (model.json)</option>
                <option value="onnx">ONNX (.onnx, onnxruntime-web)</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">Output mode</label>
              <select
                className="select select-bordered select-sm w-full"
                value={tnConfig.outputMode}
                onChange={(e) =>
                  setTnConfig({ ...tnConfig, outputMode: e.target.value })
                }
              >
                <option value="heatmap">Heatmap (single map)</option>
                <option value="argmax">Argmax over channels (BallTrackerNet 256)</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">
                Model URL ({tnConfig.backend === "onnx" ? ".onnx" : "model.json"})
              </label>
              <input
                type="text"
                className="input input-bordered input-sm w-full font-mono text-xs"
                placeholder={
                  tnConfig.backend === "onnx"
                    ? "https://…/tracknet.onnx"
                    : "https://…/tracknet/model.json"
                }
                value={tnConfig.modelUrl}
                onChange={(e) =>
                  setTnConfig({ ...tnConfig, modelUrl: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold">Input width</label>
                <input
                  type="number"
                  className="input input-bordered input-sm w-full"
                  value={tnConfig.inputWidth}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, inputWidth: +e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold">Input height</label>
                <input
                  type="number"
                  className="input input-bordered input-sm w-full"
                  value={tnConfig.inputHeight}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, inputHeight: +e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold">Frames (stack)</label>
                <input
                  type="number"
                  className="input input-bordered input-sm w-full"
                  value={tnConfig.frames}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, frames: +e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold">Heatmap threshold</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  className="input input-bordered input-sm w-full"
                  value={tnConfig.heatmapThreshold}
                  onChange={(e) =>
                    setTnConfig({
                      ...tnConfig,
                      heatmapThreshold: +e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={tnConfig.grayscale}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, grayscale: e.target.checked })
                  }
                />
                Grayscale frames
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={tnConfig.channelsFirst}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, channelsFirst: e.target.checked })
                  }
                />
                Channels-first (NCHW)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={tnConfig.reverseFrames}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, reverseFrames: e.target.checked })
                  }
                />
                Reverse frame order
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={tnConfig.bgr}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, bgr: e.target.checked })
                  }
                />
                BGR channels
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={tnConfig.normalize255 !== false}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, normalize255: e.target.checked })
                  }
                />
                Divide by 255
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={!!tnConfig.debug}
                  onChange={(e) =>
                    setTnConfig({ ...tnConfig, debug: e.target.checked })
                  }
                />
                Debug logging
              </label>
            </div>

            {tnStatus && (
              <p
                className={`text-xs ${
                  tnReady ? "text-success" : "text-base-content/60"
                }`}
              >
                {tnStatus}
              </p>
            )}

            <div className="modal-action">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowTnSettings(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary btn-sm gap-1"
                disabled={tnLoading}
                onClick={() => {
                  saveTrackNetConfig(tnConfig);
                  loadTrackNet(tnConfig);
                }}
              >
                {tnLoading && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                Load model
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowTnSettings(false)}
          />
        </div>
      )}

      {/* Live-shot confirmation overlay */}
      {pendingShot && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 pointer-events-none">
          <div className="pointer-events-auto bg-base-100 border border-base-300 shadow-xl rounded-box px-5 py-3 flex items-center gap-4">
            <div className="text-sm">
              <p className="font-semibold">Shot detected</p>
              <p className="text-base-content/60 tabular-nums">
                shot {fmt(pendingShot.time)} · video {fmt(curTime)} · ball ({pendingShot.x | 0},{" "}
                {pendingShot.y | 0})
              </p>
            </div>
            <button className="btn btn-sm btn-primary" onClick={confirmShot}>
              Confirm
            </button>
            <button className="btn btn-sm btn-ghost" onClick={skipShot}>
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BallTrackerLab;
